import React from 'react';
import { TextField, TextFieldProps, InputAdornment, IconButton, Box } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

type NumberFieldProps = Omit<TextFieldProps, 'onChange'> & {
  value: number | string | null | undefined;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  step?: number;
};

export const NumberField: React.FC<NumberFieldProps> = ({ 
  value, 
  onChange, 
  min, 
  max, 
  step = 1, 
  ...props 
}) => {
  const handleIncrement = () => {
    let val = typeof value === 'number' ? value : parseInt(value || '0', 10) || 0;
    val += step;
    if (max !== undefined && val > max) val = max;
    onChange(val.toString());
  };

  const handleDecrement = () => {
    let val = typeof value === 'number' ? value : parseInt(value || '0', 10) || 0;
    val -= step;
    if (min !== undefined && val < min) val = min;
    onChange(val.toString());
  };

  return (
    <TextField
      {...props}
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ ...props.inputProps, min, max, step }}
      InputProps={{
        ...props.InputProps,
        endAdornment: (
          <InputAdornment position="end">
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
              <IconButton 
                size="small" 
                onClick={handleIncrement} 
                sx={{ p: 0.2, height: 16 }}
                disabled={props.disabled || (max !== undefined && Number(value) >= max)}
              >
                <ExpandLess sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={handleDecrement} 
                sx={{ p: 0.2, height: 16 }}
                disabled={props.disabled || (min !== undefined && Number(value) <= min)}
              >
                <ExpandMore sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </InputAdornment>
        )
      }}
      sx={{
        '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
        ...props.sx
      }}
    />
  );
};